import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface IMenuItem {
  type: string,       // Possible values: link/dropDown/icon/separator/extLink
  name?: string,      // Used as display text for item and title for separator type
  state?: string,     // Router state
  icon?: string,      // Material icon name
  tooltip?: string,   // Tooltip text
  disabled?: boolean, // If true, item will not be appeared in sidenav.
  sub?: IChildItem[], // Dropdown items
  badges?: IBadge[],
  active?: boolean,
  codeList?: string[]
}
interface IChildItem {
  type?: string,
  name: string,       // Display text
  state?: string,     // Router state
  icon?: string,
  sub?: IChildItem[],
  active?: boolean
  code?: string,
}

interface IBadge {
  color: string;      // primary/accent/warn/hex color codes(#fff000)
  value: string;      // Display text
}

interface ISidebarState {
  sidenavOpen?: boolean;
  childnavOpen?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  public sidebarState: ISidebarState = {
    sidenavOpen: true,
    childnavOpen: false
  }
  constructor() { }

  defaultMenu: IMenuItem[] = [
    {
      name: 'Admin',
      type: 'dropDown',
      icon: 'admin_menu.png',
      codeList: ['ROLR', 'USRR', 'STUR', 'GRDR', 'SUBR', 'IMGR', 'ATCR', 'ATLR', 'ADMIN'],
      sub: [
        { icon: 'icon-activity', name: 'Roles', state: '/Role', type: 'link', code: 'ROLR' },
        { icon: 'icon-users', name: 'Users', state: '/User', type: 'link', code: 'USRR' },
        { icon: 'icon-user-group', name: 'Students', state: '/Students', type: 'link', code: 'STUR' },
        { icon: 'icon-bookmark1', name: 'Grades', state: '/Grades', type: 'link', code: 'GRDR' },
        { icon: 'icon-edit', name: 'Subjects', state: '/Subjects', type: 'link', code: 'SUBR' },
        { icon: 'icon-image1', name: 'Image Upload', state: '/ImageUpload', type: 'link', code: 'IMGR' },
        { icon: 'icon-cogs', name: 'Activity Config', state: '/ActivityConfig', type: 'link', code: 'ATCR' },
        { icon: 'icon-package', name: 'Activity Label', state: '/ActivityLabel', type: 'link', code: 'ATLR' },
        { icon: 'icon-news-paper', name: 'Curriculums', state: '/Curriculums', type: 'link', code: 'ADMIN' },
        { icon: 'icon-image1', name: 'Image Config', state: '/ImageConfig', type: 'link', code: 'SISIMG' },
      ]
    },
    {
      name: 'User Parent',
      type: 'dropDown',
      icon: 'user_parent.png',
      codeList: ['ADMIN'],
      sub: [
        { icon: 'icon-book-open', name: 'Schools', state: '/Schools', type: 'link', code: 'ADMIN' },
      ]
    },
    {
      name: 'Learning Platform',
      type: 'dropDown',
      icon: 'learning_platform.png',
      codeList: ['TOPR', 'SKLR', 'QUSR'],
      sub: [
        { icon: 'icon-grid', name: 'Topics', state: '/Topics', type: 'link', code: 'TOPR' },
        { icon: 'icon-zap', name: 'Skills', state: '/Skills', type: 'link', code: 'SKLR' },
        { icon: 'icon-clipboard1', name: 'Questions', state: '/Questions', type: 'link', code: 'QUSR' },
      ]
    },
    {
      name: 'Learning Platform Reports',
      type: 'dropDown',
      icon: 'learning_platform_report.png',
      codeList: ['LESR', 'WCKR', 'ISPR', 'CWAR', 'CDAR'],
      sub: [
        { icon: 'icon-trending-up', name: 'Learning Summary', state: '/LearningSummary', type: 'link', code: 'LESR' },
        { icon: 'icon-activity', name: 'Class Performance Report', state: '/ClassPerformanceReport', type: 'link', code: 'WCKR' },
        { icon: 'icon-bar-chart', name: 'Student Performance Report', state: '/student-performance-report', type: 'link', code: 'ISPR' },
        { icon: 'icon-barcode', name: 'Topic-wise Performance Report', state: '/topic-wise-performance-report', type: 'link', code: 'CWAR' },
        { icon: 'icon-crop', name: 'Cognitive Performance Report', state: '/cognitive-performance-report', type: 'link', code: 'CDAR' },
      ]
    },
    {
      name: 'Assessments',
      type: 'dropDown',
      icon: 'assessment.png',
      codeList: ['ASSR', 'TBAR', 'TBQR'],
      sub: [
        { icon: 'icon-file-text2', name: 'Assessments', state: '/Assessments', type: 'link', code: 'ASSR' },
        { icon: 'icon-alarm', name: 'Timebound Assessments', state: '/timeboundAssessment', type: 'link', code: 'TBAR' },
        { icon: 'icon-history', name: 'Timebound Questions', state: '/timeboundQuestions', type: 'link', code: 'TBQR' },
      ]
    },
    {
      name: 'Assessment Reports',
      type: 'dropDown',
      icon: 'assessment_report.png',
      codeList: ['ALSR', 'WCAR', 'ISAR', 'CWTR', 'CDTR'],
      sub: [
        { icon: 'icon-trending-up', name: 'Learning Summary', state: '/assessment-student-summary', type: 'link', code: 'ALSR' },
        { icon: 'icon-activity', name: 'Class Assessment Report', state: '/class-assessment-report', type: 'link', code: 'WCAR' },
        { icon: 'icon-bar-chart', name: 'Student Assessment Report', state: '/student-assessment-report', type: 'link', code: 'ISAR' },
        { icon: 'icon-barcode', name: 'Topic-wise Assessment Report', state: '/topic-wise-assessment-report', type: 'link', code: 'CWTR' },
        { icon: 'icon-crop', name: 'Cognitive Assessment Report', state: '/cognitive-assessment-report', type: 'link', code: 'CDTR' },
      ]
    },
    {
      name: 'School Analytics',
      type: 'dropDown',
      icon: 'school_analytics.png',
      codeList: ['ATUR', 'CPAR', 'CWPR', 'WCPR', 'GWAR'],
      sub: [
        { icon: 'icon-upload', name: 'Activity Upload', state: '/ActivityUpload', type: 'link', code: 'ATUR' },
        { icon: 'icon-chart', name: 'Class Radar Report', state: '/ClassRadarReport', type: 'link', code: 'CPAR' },
        { icon: 'icon-bar-chart-2', name: 'Classwise Performance', state: '/ClasswisePerformance', type: 'link', code: 'CWPR' },
        { icon: 'icon-cog', name: 'Term Analysis Report', state: '/term-analysis-report', type: 'link', code: 'WCPR' },
        { icon: 'icon-user-group', name: 'Comparative Analysis Report', state: '/comparative-analysis-report', type: 'link', code: 'GWAR' },
      ]
    },
    {
      name: 'CPD',
      type: 'dropDown',
      icon: 'cpd.png',
      codeList: ['TCHR', 'CATR', 'DOMR', 'CTOR', 'STOR', 'CQUR', 'CAAW', 'CTAR', 'CTQR'],
      sub: [
        { icon: 'icon-users1', name: 'Teacher', state: '/teachers', type: 'link', code: 'TCHR' },
        { icon: 'icon-tree', name: 'Categories', state: '/cpd-categories', type: 'link', code: 'DOMR' },
        { icon: 'icon-package', name: 'Domains', state: '/cpd-domains', type: 'link', code: 'CATR' },
        { icon: 'icon-command1', name: 'Topics', state: '/cpd-topics', type: 'link', code: 'CTOR' },
        { icon: 'icon-list1', name: 'Sub Topics', state: '/cpd-subtopics', type: 'link', code: 'STOR' },
        { icon: 'icon-queue', name: 'Questions', state: '/cpd-questions', type: 'link', code: 'CQUR' },
        { icon: 'icon-pocket', name: 'Assessments', state: '/cpd-assessments', type: 'link', code: 'CAAW' },
        { icon: 'icon-alarm', name: 'Timebound-Assessments', state: '/cpd-timebound-assessments', type: 'link', code: 'CTAR' },
        { icon: 'icon-wind', name: 'Timebound-Questions', state: '/cpd-timebound-questions', type: 'link', code: 'CTQR' },
        { icon: 'icon-cpu', name: 'Lesson Observation Tool', state: '/lesson-observation-tool', type: 'link', code: 'LOT' },
        { icon: 'icon-brightness-down', name: 'SSAR', state: '/ssar', type: 'link', code: 'SSAR' },
      ]
    },
    {
      name: 'Learning Objective Tracker',
      type: 'dropDown',
      icon: 'learning_objective_tracker.png',
      codeList: ['MONR', 'MBKR'],
      sub: [
        { icon: 'icon-document', name: 'Objectives', state: '/objectives', type: 'link', code: 'MONR' },
        { icon: 'icon-book-reference', name: 'Tracker', state: '/tracker', type: 'link', code: 'MBKR' },
        { icon: 'icon-book-reference', name: 'Tracker v2', state: '/tracker-v2', type: 'link', code: 'MBKR' },
        { icon: 'icon-align-left1', name: 'LO Progress Summary Report', state: '/lo-progress-summary-report', type: 'link', code: 'PSRR' },
        { icon: 'icon-chart-pie', name: 'LO Progress Cricle Report', state: '/lo-progress-circle-report', type: 'link', code: 'PCRR' },
        { icon: 'icon-book-reference', name: 'Individual Student Progress Report', state: '/individual-student-progress-report', type: 'link', code: 'SPRR' },
        { icon: 'icon-radar', name: 'Analysis Config', state: '/analysis-config', type: 'link', code: 'SISIMG' },
      ]
    },
    {
      name: 'Bench Mark',
      type: 'dropDown',
      icon: 'learning_objective_tracker.png',
      codeList: ['ADMIN'],
      sub: [
        { icon: 'icon-document', name: 'Benck Marks', state: '/benchmarks', type: 'link', code: 'ADMIN' },
        { icon: 'icon-book-reference', name: 'Benck Mark Questions', state: '/benchmarkquestions', type: 'link', code: 'ADMIN' }
      ]
    }
  ]


  // sets iconMenu as default;
  menuItems = new BehaviorSubject<IMenuItem[]>(this.defaultMenu);
  // navigation component has subscribed to this Observable
  menuItems$ = this.menuItems.asObservable();
}
